// The tab store for a logged-out visitor's medications (STACK-565).
//
// Design: docs/guest-medication-check.md §3 (data flow) and §3.1. The rules
// this module exists to keep, all ruled:
//
//   - The medications live in `sessionStorage`, one key, and nowhere else in
//     the browser: never `localStorage`, a cookie or a URL (Tanu, 2026-08-18,
//     privacy 11–13). `sessionStorage` dies with the tab and survives the
//     full page loads `CreateStack.saveStateAndNavigate` does, which React
//     state would not. Analytics is the one other place they go: Mixpanel
//     receives an added medication's code and name, and the check's result
//     per pair (STACK-580, Tanu 2026-10-12, amending rule 12).
//   - Each item is `{ in_rxcui, ingredient_name, display_name }` and nothing
//     more. The server resolves names itself from RxNorm; these are only for
//     showing the visitor what they added.
//   - At most GUEST_INTERACTION_MAX_MEDICATIONS. The backend holds the same
//     number (`endpoints/guest_interaction.py`); a backend test reads this
//     file and keeps the two equal.
//   - Signing in or up empties it (`AuthContext`).
//
// Nothing else in the app reads or writes the key.

export const GUEST_INTERACTION_MAX_MEDICATIONS = 5;

const STORAGE_KEY = 'guestMedications';

const listeners = new Set();

const storage = () => {
  try {
    return window.sessionStorage;
  } catch (_) {
    // Storage blocked (privacy mode, sandboxed WebView): the feature simply
    // holds nothing, and the composer behaves as if none were added.
    return null;
  }
};

const isPositiveInt = (v) => Number.isInteger(v) && v > 0;

// Only the three fields, and only well-formed ones: whatever else a caller
// passes (a search result, a details response) never reaches storage.
const clean = (item) => {
  if (!item || typeof item !== 'object') return null;
  const in_rxcui = typeof item.in_rxcui === 'string' ? parseInt(item.in_rxcui, 10) : item.in_rxcui;
  if (!isPositiveInt(in_rxcui)) return null;
  const ingredient_name = typeof item.ingredient_name === 'string' ? item.ingredient_name : '';
  const display_name = typeof item.display_name === 'string' && item.display_name
    ? item.display_name
    : ingredient_name;
  if (!display_name) return null;
  return { in_rxcui, ingredient_name, display_name };
};

const cleanList = (list) => (Array.isArray(list) ? list : [])
  .map(clean).filter(Boolean).slice(0, GUEST_INTERACTION_MAX_MEDICATIONS);

// The key holds `{ items, checked }`: `items` is what the composer shows and
// edits; `checked` is `{ stackId, items }`, the list one generated stack was
// checked against, so a stack restored after a supplement page keeps its own
// medications even if the composer's changed since (PR 390 review). A bare
// array is the earlier shape, read as `items`.
const readState = () => {
  const s = storage();
  if (!s) return { items: [], checked: null };
  try {
    const parsed = JSON.parse(s.getItem(STORAGE_KEY) || '[]');
    if (Array.isArray(parsed)) return { items: cleanList(parsed), checked: null };
    const checked = parsed?.checked && typeof parsed.checked.stackId === 'string'
      ? { stackId: parsed.checked.stackId, items: cleanList(parsed.checked.items) }
      : null;
    return { items: cleanList(parsed?.items), checked };
  } catch (_) {
    return { items: [], checked: null };
  }
};

const writeState = ({ items, checked }) => {
  const s = storage();
  if (!s) return;
  try {
    if (items.length || checked?.items.length) s.setItem(STORAGE_KEY, JSON.stringify({ items, checked }));
    else s.removeItem(STORAGE_KEY);
  } catch (_) {
    // Quota or blocked storage: nothing to do but not crash the composer.
  }
};

export const getGuestMedications = () => readState().items;

const write = (items) => {
  writeState({ ...readState(), items });
  listeners.forEach((fn) => fn(items));
};

/**
 * Remembers the medications a stack was checked against, in this tab only.
 * One stack at a time: the visitor's latest.
 */
export const rememberCheckedMedications = (stackId, items) => {
  if (!stackId) return;
  writeState({ ...readState(), checked: { stackId, items: cleanList(items) } });
};

/** The medications `stackId` was checked against, or null if not remembered. */
export const recallCheckedMedications = (stackId) => {
  const { checked } = readState();
  return checked && checked.stackId === stackId ? checked.items : null;
};

/**
 * Adds a medication. Returns `'added'`, `'duplicate'`, `'full'` or `'invalid'`
 * so the panel can say what happened without re-reading the store.
 */
export const addGuestMedication = (item) => {
  const next = clean(item);
  if (!next) return 'invalid';
  const current = getGuestMedications();
  if (current.some((m) => m.in_rxcui === next.in_rxcui)) return 'duplicate';
  if (current.length >= GUEST_INTERACTION_MAX_MEDICATIONS) return 'full';
  write([...current, next]);
  return 'added';
};

/** @returns {number|null} remaining count after a real removal, else null */
export const removeGuestMedication = (inRxcui) => {
  const current = getGuestMedications();
  const next = current.filter((m) => m.in_rxcui !== inRxcui);
  if (next.length === current.length) return null;
  write(next);
  return next.length;
};

export const clearGuestMedications = () => {
  const s = storage();
  try {
    s?.removeItem(STORAGE_KEY);
  } catch (_) {
    // Blocked storage holds nothing to clear.
  }
  listeners.forEach((fn) => fn([]));
};

/** Calls `fn(items)` on every change made in this tab. Returns an unsubscribe. */
export const subscribeGuestMedications = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/**
 * The shape `getMedicationDisplayName` and `createEnhancedInteractionData`
 * read (`identifiers.in_rxcui`, `name.display`), so the members' flag UI can
 * name a visitor's medications without knowing where they came from.
 */
export const asDisplayMedications = (items) =>
  (items || []).map((m) => ({
    identifiers: { in_rxcui: m.in_rxcui },
    name: { display: m.display_name },
  }));
